import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from 'react';

/**
 * AudioPlayer will play the audio source provided in the `src` prop.
 * If src is Array<string> it'll play through them as a sequence.
 */

interface AudioPlayerProps {
  src: string | Array<string>;
  pause?: number;
  muted?: boolean;
  onLoadSrc?: (src: string) => void;
  onReadySrc?: (src: string) => void;
  onLoadTimeoutSrc?: (src: string) => void;
  onPlaySrc?: (src: string, index: number) => void;
  onEnded?: () => void;
}

export interface AudioPlayer {
  play: () => void;
  pause: () => void;
}

const AUDIO_PLAYER_LOAD_TIMEOUT = 10000;

export const AudioPlayer = forwardRef<AudioPlayer, AudioPlayerProps>(
  (
    {
      src,
      pause = 0,
      muted = false,
      onLoadSrc,
      onReadySrc,
      onLoadTimeoutSrc,
      onPlaySrc,
      onEnded,
    },
    ref,
  ) => {
    const audioRef = useRef<HTMLAudioElement>(null);
    const [isPlaying, setIsPlaying] = useState<boolean>(false);
    const [playingIndex, setPlayingIndex] = useState<number>(0);
    const [audioSrc, setAudioSrc] = useState<string | undefined>();

    const doSetAudioSrc = useCallback(
      (src: string) => {
        setAudioSrc(src);
        onLoadSrc?.(src);
      },
      [onLoadSrc],
    );

    useImperativeHandle(ref, () => ({
      play: () => {
        setIsPlaying(true);
      },
      pause: () => {
        audioRef.current?.pause();
      },
    }));

    const handleReachedEndOfSrc = useCallback(() => {
      if (!audioSrc) {
        return;
      }

      if (!Array.isArray(src)) {
        setIsPlaying(false);
        onEnded?.();
        setPlayingIndex(0);

        return;
      }

      // const endedIndex = src.indexOf(audioSrc);

      setTimeout(() => {
        if (playingIndex === src.length - 1) {
          setIsPlaying(false);
          onEnded?.();
          doSetAudioSrc(src[0]);
          setPlayingIndex(0);
        } else {
          doSetAudioSrc(src[playingIndex + 1]);
          setPlayingIndex(playingIndex + 1);
        }
      }, pause);
    }, [audioSrc, doSetAudioSrc, onEnded, pause, playingIndex, src]);

    useEffect(() => {
      const srcToPlay = Array.isArray(src) ? src[playingIndex] : src;

      doSetAudioSrc(srcToPlay);

      const audioPlayer = audioRef.current;

      const timeout = setTimeout(() => {
        const HAVE_FUTURE_DATA = 3;

        if (audioPlayer && audioPlayer.readyState < HAVE_FUTURE_DATA) {
          onLoadTimeoutSrc?.(srcToPlay);
        }
      }, AUDIO_PLAYER_LOAD_TIMEOUT);

      return () => {
        clearTimeout(timeout);
      };
    }, [src, playingIndex, onLoadTimeoutSrc, doSetAudioSrc]);

    useEffect(() => {
      const audioPlayer = audioRef.current;

      audioPlayer?.addEventListener('ended', handleReachedEndOfSrc);

      return () => {
        audioPlayer?.removeEventListener('ended', handleReachedEndOfSrc);
      };
    }, [handleReachedEndOfSrc]);

    useEffect(() => {
      if (!audioSrc) {
        return;
      }

      if (!isPlaying) {
        audioRef.current?.pause();

        return;
      }

      audioRef.current?.play();

      onPlaySrc?.(audioSrc, Array.isArray(src) ? playingIndex : 0);
    }, [isPlaying, audioSrc, src, onPlaySrc, playingIndex]);

    return (
      <audio
        ref={audioRef}
        src={audioSrc?.indexOf('http') === 0 ? audioSrc : `/phonics${audioSrc}`}
        onCanPlayThrough={() => audioSrc && onReadySrc?.(audioSrc)}
        preload="auto"
        muted={muted}
      >
        Your browser does not support the audio element.
      </audio>
    );
  },
);

AudioPlayer.displayName = 'AudioPlayer';
